/**
 * Bridges iubenda consent preferences to Shopify's Customer Privacy API, so
 * Shopify (and any sales channel / pixel relying on it) follows what was
 * decided in the iubenda banner.
 *
 * Wired via `_iub.csConfiguration.callback.onPreferenceExpressed` in
 * snippets/iubenda.liquid. Purpose mapping per iubenda's Shopify guide
 * (https://www.iubenda.com/en/help/145731), with the rules from
 * https://enricochiolo.substack.com/p/come-funziona-la-consent-di-shopify:
 * every write carries the three GDPR signals (omitted keys would silently
 * keep their previous registry state), while `sale_of_data` — a US-privacy
 * concept iubenda never asks GDPR visitors about — is only written when a US
 * privacy choice actually expressed it, exactly like the legacy theme. The
 * registry is only written when the mapped consent differs from it — iubenda
 * replays
 * `onPreferenceExpressed` on every page load for returning visitors, and a
 * redundant write would fire `visitorConsentCollected` to every subscribed
 * app and pixel each time.
 *
 * When `window.iubendaForceAnalyticsConsent` is set (the
 * `iubenda_force_analytics_consent` theme setting), the analytics signal
 * reported to Shopify is forced to granted regardless of the shopper's
 * choice, reproducing the legacy production behavior — iubenda's own stored
 * preference keeps the real choice. When Google Tag Manager lands in this
 * theme, its Consent Mode v2 update should hook into the end of
 * `iubendaShopifyBridge` and mirror the same override (the legacy
 * `consentModeV2` also forced `analytics_storage: 'granted'`); note Consent
 * Mode is a separate layer that Shopify's registry does not drive for
 * external GTM tags.
 */

// iubenda purpose codes (numbers = GDPR purposes, strings = US privacy
// choices) mapped onto Shopify's four consent categories.
const SHOPIFY_PURPOSES = {
  analytics: [4, 's'],
  marketing: [5, 'adv'],
  preferences: [2, 3],
  sale_of_data: ['s', 'sh'],
};

// `window.Shopify.loadFeatures` is defined by `content_for_header` scripts,
// which render after the iubenda snippet — poll briefly instead of assuming
// it exists.
function whenShopifyReady(callback, attempt = 0) {
  if (window.Shopify?.loadFeatures) {
    callback();
  } else if (attempt < 50) {
    setTimeout(() => whenShopifyReady(callback, attempt + 1), 100);
  }
}

function mapConsent(consent) {
  const expressedConsent = {};
  for (const [category, codes] of Object.entries(SHOPIFY_PURPOSES)) {
    let expressed = null;
    for (const code of codes) {
      if (consent.purposes && typeof consent.purposes[code] === 'boolean') {
        expressed = consent.purposes[code];
      }
      if (
        consent.uspr &&
        typeof consent.uspr[code] === 'boolean' &&
        expressed !== false
      ) {
        expressed = consent.uspr[code];
      }
    }
    if (typeof expressed === 'boolean') {
      expressedConsent[category] = expressed;
    }
  }
  return expressedConsent;
}

window.iubendaShopifyBridge = function (preference) {
  const hasPreference = preference && Object.keys(preference).length > 0;
  const consent = hasPreference ? preference : window._iub.cs.api.getPreferences();
  const expressedConsent = mapConsent(consent);

  if (Object.keys(expressedConsent).length === 0) return;

  // The three GDPR signals count as refused when never expressed;
  // `sale_of_data` maps only from US-privacy choices and stays out of the
  // write when unexpressed, so GDPR visitors keep it unset in the registry.
  expressedConsent.analytics ??= false;
  expressedConsent.marketing ??= false;
  expressedConsent.preferences ??= false;

  if (window.iubendaForceAnalyticsConsent) {
    expressedConsent.analytics = true;
  }

  whenShopifyReady(() => {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) throw error;

        const registry = window.Shopify.customerPrivacy.currentVisitorConsent();
        const changed = Object.entries(expressedConsent).some(
          ([category, granted]) => registry[category] !== (granted ? 'yes' : 'no')
        );
        if (!changed) return;

        window.Shopify.customerPrivacy.setTrackingConsent(expressedConsent, () => {});
      }
    );
  });
};

// A preference expressed before this module loaded is parked by the snippet's
// callback — process it now.
if (window.iubendaPendingPreference) {
  window.iubendaShopifyBridge(window.iubendaPendingPreference);
  delete window.iubendaPendingPreference;
}

// Shopify menu items can't carry the `iubenda-cs-preferences-link` class that
// iubenda binds automatically, so any link pointing at the sentinel URL
// `#iubenda-preferences` (set on the menu item in Shopify admin navigation)
// reopens the preferences modal instead of navigating.
document.querySelectorAll('a[href$="#iubenda-preferences"]').forEach((link) => {
  link.addEventListener('click', (event) => {
    event.preventDefault();
    window._iub?.cs?.api?.openPreferences();
  });
});
